import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import { useIsModuleEnabled } from 'src/hooks/useIsModuleEnabled';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useGetOversightAssessmentCount } from '@/hooks/queries';

/**
 * Gates the optional Oversight rating columns on the Risk Register and Compliance Register.
 *
 * Returns true when either:
 *   - the user's role carries `read:oversight_assessment` AND the
 *     Oversight module is enabled for the org, OR
 *   - the user is owner/contributor on at least one oversight assessment
 *     (visible via Hasura row-level perms). In this case the module flag is
 *     intentionally ignored: if the user is already assigned to a CMA, the
 *     data exists for them and they should be able to surface its rating
 *     regardless of the org's module toggle.
 *
 * While the permission query is still loading, the aggregate count query is
 * deferred so role-permission users (who don't need the count) don't fire it
 * unnecessarily. Returns `false` during loading — column config will hydrate
 * once the decision is stable.
 */
export const useCanViewOversightColumns = (): boolean => {
  const oversightModuleEnabled = useIsModuleEnabled(
    'assessment.subModules.oversight_register_and_findings'
  );

  const { hasPermission: hasRolePermission, loading: permissionLoading } =
    useHasPermissionQuery('read:oversight_assessment');

  const roleGrants = hasRolePermission && oversightModuleEnabled;

  // Only probe for contributor rows if the role can select oversight assessments
  // at all — otherwise the aggregate 404s with `field 'oversight_assessment' not
  // found in type: 'query_root'` for roles that can't read it (RSP-6542).
  // The catalogue now carries the post-rename `oversight_assessment` spelling
  // and the Oversight roles are granted against it (COI-639); the legacy
  // `compliance_monitoring_assessment` grant was removed with ComplianceManager's,
  // so gating on the old name resolves false for every role. `true` =
  // "role has ANY oversight read grant" (owner/contributor/any).
  const { hasPermission: canSelectOversight, loading: oversightSelectLoading } =
    useHasPermissionQuery('read:oversight_assessment', undefined, true);

  // The same `entityIds` every nav badge passes (ConnectedCount.tsx). All 19
  // badge hooks share one query key so React Query dedupes them onto a single
  // request (COI-908); passing `{}` here would fork the key and fire a second
  // call that runs all 26 counts. It narrows nothing — `oversightAssessment`
  // is a global register server-side, and the GraphQL leg declares no
  // variables.
  const { entityIds } = useEntityFilter();

  // Bound once and applied twice, deliberately. Sharing the nav's query key
  // means a DISABLED query still reads its cached data, so `skip` no longer
  // implies `data === undefined`. Gating the result as well keeps this a fetch
  // optimisation rather than silent control flow.
  const shouldSkip =
    permissionLoading ||
    oversightSelectLoading ||
    roleGrants ||
    !canSelectOversight;

  // `cache-first` preserves the policy this call site had under Apollo's
  // default; the factory defaults to `no-cache`.
  const { data } = useGetOversightAssessmentCount({
    queryArgs: { entityIds },
    shouldSkip,
    fetchPolicy: 'cache-first',
  });

  const hasContributions =
    !shouldSkip &&
    (data?.oversight_assessment_aggregate.aggregate?.count ?? 0) > 0;

  if (permissionLoading) {
    return false;
  }

  return roleGrants || hasContributions;
};
